import { Cell, Grid, HeadingSection, InfoLabel, Text } from 'bold-ui'
import { booleanToSimNaoTitleCase } from 'components/form'
import { Cbo, Escolaridade, InformacoesSociodemograficas } from 'graphql/types.generated'
import { isObjectDeepEmpty } from 'util/object'
import { formatSimNaoJoinRespostas } from 'view/cidadao/utils-cidadao'

import {
  criancas0a9anosComQuemFicaRecord,
  deficienciaRecord,
  povoComunidadeTradicionalRecord,
  situacaoMercadoTrabalhoRecord,
  tempoAtendimentoSocioeducativoRecord,
  tipoAtendimentoSocioeducativoRecord,
} from '../../create/types/model-cidadao'

interface InformacoesSociodemograficasSectionProps {
  informacoesSociodemograficas: InformacoesSociodemograficas
  escolaridade: Escolaridade
  cbo: Cbo
  isAtendimentoSocioeducativoEnabled: boolean
}

export const InformacoesSociodemograficasSection = (props: InformacoesSociodemograficasSectionProps) => {
  const { informacoesSociodemograficas, escolaridade, cbo, isAtendimentoSocioeducativoEnabled } = props

  const { id: _id, __typename, ...rest } = informacoesSociodemograficas ?? {}
  const hasAtendimentoSocioeducativo =
    isAtendimentoSocioeducativoEnabled && informacoesSociodemograficas?.isAtendimentoSocioeducativo != null
  const isAdolescenteAtendimentoSocioeducativo = informacoesSociodemograficas?.isAtendimentoSocioeducativo === true
  const isInformacoesSociodemograficasVazio =
    isObjectDeepEmpty(rest) && escolaridade == null && cbo == null && !hasAtendimentoSocioeducativo

  if (isInformacoesSociodemograficasVazio)
    return (
      <HeadingSection level={2} title='Informações sociodemográficas'>
        <Text fontStyle='italic'>Sem informações registradas</Text>
      </HeadingSection>
    )

  return (
    <HeadingSection level={2} title='Informações sociodemográficas'>
      <Grid wrap>
        <Cell size={4}>
          <InfoLabel title='Escolaridade'>{escolaridade?.nome}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Frequenta escola ou creche'>
            {booleanToSimNaoTitleCase(informacoesSociodemograficas?.frequentaEscolaOuCreche)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Com quem fica a criança (Menores de 10 anos)'>
            {informacoesSociodemograficas?.criancas0a9anosComQuemFica
              ?.map((item) => criancas0a9anosComQuemFicaRecord[item])
              .sort()
              .join(', ')}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Situação no mercado de trabalho'>
            {situacaoMercadoTrabalhoRecord[informacoesSociodemograficas?.situacaoMercadoTrabalho]}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Ocupação'>{cbo?.nome.titleCase()}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Plano de saúde privado'>
            {booleanToSimNaoTitleCase(informacoesSociodemograficas?.possuiPlanoSaudePrivado)}
          </InfoLabel>
        </Cell>
        {hasAtendimentoSocioeducativo && (
          <>
            <Cell size={4}>
              <InfoLabel title='Adolescente em atendimento socioeducativo'>
                {booleanToSimNaoTitleCase(informacoesSociodemograficas?.isAtendimentoSocioeducativo)}
              </InfoLabel>
            </Cell>
            {isAdolescenteAtendimentoSocioeducativo && (
              <>
                <Cell size={4}>
                  <InfoLabel title='Tipo de atendimento socioeducativo'>
                    {tipoAtendimentoSocioeducativoRecord[informacoesSociodemograficas?.tipoAtendimentoSocioeducativo]}
                  </InfoLabel>
                </Cell>
                <Cell size={4}>
                  <InfoLabel title='Tempo de atendimento'>
                    {tempoAtendimentoSocioeducativoRecord[informacoesSociodemograficas?.tempoAtendimentoSocioeducativo]}
                  </InfoLabel>
                </Cell>
              </>
            )}
          </>
        )}
        <Cell size={4}>
          <InfoLabel title='Frequenta cuidador tradicional'>
            {booleanToSimNaoTitleCase(informacoesSociodemograficas?.frequentaCuidadorTradicional)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Participa de grupo comunitário'>
            {booleanToSimNaoTitleCase(informacoesSociodemograficas?.participaAlgumGrupoComunitario)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Membro de Povo ou Comunidade Tradicional ou Campo, Floresta e Água'>
            {formatSimNaoJoinRespostas(
              informacoesSociodemograficas?.isMembroComunidadeTradicional,
              povoComunidadeTradicionalRecord[informacoesSociodemograficas?.povoComunidadeTradicional]
            )}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Possui alguma deficiência'>
            {formatSimNaoJoinRespostas(
              informacoesSociodemograficas?.possuiDeficiencia,
              informacoesSociodemograficas?.deficiencias.map((item) => deficienciaRecord[item])
            )}
          </InfoLabel>
        </Cell>
      </Grid>
    </HeadingSection>
  )
}
